/* Happy Little Doers — Web Components (vanilla ES6+, no dependencies).
   Loaded once via <script src="hld-theme.js" defer> from hld-header (or any section).
   All definitions are guarded so multiple section includes never double-register. */
(function () {
  'use strict';

  /* ---- Scroll lock ------------------------------------------------------
     Locks <html>, never <body>. The header group is position: sticky, and an
     `overflow: hidden` on body would turn body into a scroll container — sticky
     would then resolve against body's (unscrolled) box and the header would
     vanish to the top of the document whenever the drawer or menu opened.
     html[scroll-lock] propagates to the viewport instead, which freezes the page
     while leaving the scroll position — and the sticky header — where they are. */
  const setScrollLock = (on) => {
    document.documentElement.toggleAttribute('scroll-lock', !!on);
  };

  /* ---- Sticky header ----------------------------------------------------
     The CSS lives in hld-tokens.css; this only feeds it measurements. The whole
     group pins at top: 0 — announcement bar included — so the £50 free shipping
     line never leaves the viewport. --hld-announcement-h + --hld-header-h drive
     scroll-padding-top so in-page anchors clear the pinned bar and header. */
  if (!window.__hldStickyHeader) {
    window.__hldStickyHeader = true;
    const initSticky = () => {
      const group = document.getElementById('header-group');
      if (!group) return;
      const bar = group.querySelector('.hld-announcement-section');
      const header = group.querySelector('.hld-header');
      const root = document.documentElement;

      const measure = () => {
        const barH = bar ? bar.offsetHeight : 0;
        root.style.setProperty('--hld-announcement-h', barH + 'px');
        if (header) root.style.setProperty('--hld-header-h', header.offsetHeight + 'px');
      };

      let ticking = false;
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          // The bar no longer scrolls away, so 'stuck' means "the page has moved
          // at all" — that is the moment the transparent homepage header needs
          // its solid band and the shadow. A few px of slack absorbs the rubber
          // -band overscroll iOS reports as a positive scrollY at rest.
          root.toggleAttribute('data-hld-stuck', window.scrollY > 4);
        });
      };

      measure();
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
      // The bar wraps to two lines on narrow screens and the nav pill reflows, so
      // the offsets have to be re-read on resize, not just once at load.
      if ('ResizeObserver' in window) {
        const ro = new ResizeObserver(measure);
        ro.observe(group);
        if (bar) ro.observe(bar);
        if (header) ro.observe(header);
      } else {
        window.addEventListener('resize', measure);
      }
    };
    document.readyState === 'loading'
      ? document.addEventListener('DOMContentLoaded', initSticky)
      : initSticky();
  }

  /* ---- Scroll reveal ---------------------------------------------------- */
  if (!window.__hldReveal) {
    window.__hldReveal = true;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const init = () => {
      const els = document.querySelectorAll('.hld-reveal');
      if (reduce || !('IntersectionObserver' in window)) {
        els.forEach((el) => el.classList.add('is-in'));
        return;
      }
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
      els.forEach((el) => io.observe(el));
    };
    document.readyState === 'loading'
      ? document.addEventListener('DOMContentLoaded', init)
      : init();
  }

  /* ---- <hld-accordion> : FAQ / expandable rows -------------------------- */
  if (!customElements.get('hld-accordion')) {
    customElements.define('hld-accordion', class extends HTMLElement {
      connectedCallback() {
        this.querySelectorAll('[data-hld-acc-trigger]').forEach((btn) => {
          const panel = btn.nextElementSibling;
          if (!panel) return;
          btn.setAttribute('aria-expanded', 'false');
          panel.hidden = true;
          btn.addEventListener('click', () => {
            const open = btn.getAttribute('aria-expanded') === 'true';
            btn.setAttribute('aria-expanded', String(!open));
            panel.hidden = open;
          });
        });
      }
    });
  }

  /* ---- <hld-gallery> : PDP main image + thumbnail switcher -------------- */
  if (!customElements.get('hld-gallery')) {
    customElements.define('hld-gallery', class extends HTMLElement {
      connectedCallback() {
        this.main = this.querySelector('[data-hld-gallery-main] img');
        this.thumbs = [...this.querySelectorAll('[data-hld-gallery-thumb]')];
        this.thumbs.forEach((t) => {
          t.addEventListener('click', () => this.select(t));
          t.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.select(t); }
          });
        });
      }
      select(thumb) {
        const img = thumb.querySelector('img');
        if (this.main && img) {
          this.main.src = img.currentSrc || img.src;
          if (img.srcset) this.main.srcset = img.srcset;
          this.main.alt = img.alt;
        }
        this.thumbs.forEach((t) => t.setAttribute('aria-current', t === thumb ? 'true' : 'false'));
      }
    });
  }

  /* ---- <hld-sticky-cta> : mobile add-to-cart bar reveal ----------------- */
  if (!customElements.get('hld-sticky-cta')) {
    customElements.define('hld-sticky-cta', class extends HTMLElement {
      connectedCallback() {
        const anchorId = this.getAttribute('data-anchor');
        const anchor = anchorId && document.getElementById(anchorId);
        if (!anchor || !('IntersectionObserver' in window)) { this.hidden = false; return; }
        const io = new IntersectionObserver((entries) => {
          // show the bar once the main CTA has scrolled out of view
          this.classList.toggle('is-visible', !entries[0].isIntersecting);
        }, { threshold: 0 });
        io.observe(anchor);
      }
    });
  }

  /* ---- <hld-media-hover> : step through product media as you hover ------ */
  if (!customElements.get('hld-media-hover')) {
    customElements.define('hld-media-hover', class extends HTMLElement {
      connectedCallback() {
        this.imgs = [...this.querySelectorAll('.hld-pcard__img')];
        if (this.imgs.length < 2) return;
        this.card = this.closest('.hld-pcard');
        this.dots = this.card ? [...this.card.querySelectorAll('.hld-pcard__dot')] : [];
        this.i = 0;
        // Only fine pointers (mouse) get the position-based scrub.
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        // Listen on the media box so the stretched nav-link on top doesn't swallow moves.
        this.zone = this.closest('.hld-pcard__media') || this;
        this.zone.addEventListener('pointermove', (e) => this.onMove(e));
        this.zone.addEventListener('pointerleave', () => this.show(0));
      }

      onMove(e) {
        const rect = this.zone.getBoundingClientRect();
        if (!rect.width) return;
        const ratio = (e.clientX - rect.left) / rect.width;
        let idx = Math.floor(ratio * this.imgs.length);
        if (idx < 0) idx = 0;
        if (idx > this.imgs.length - 1) idx = this.imgs.length - 1;
        this.show(idx);
      }

      show(idx) {
        if (idx === this.i || !this.imgs[idx]) return;
        this.imgs[this.i].classList.remove('is-active');
        this.imgs[this.i].setAttribute('aria-hidden', 'true');
        this.imgs[idx].classList.add('is-active');
        this.imgs[idx].removeAttribute('aria-hidden');
        if (this.dots[this.i]) this.dots[this.i].classList.remove('is-active');
        if (this.dots[idx]) this.dots[idx].classList.add('is-active');
        this.i = idx;
      }
    });
  }

  /* ---- <hld-quick-add> : AJAX add-to-cart for product cards -------------
     Also carries the PDP "Add accessories" multi-add. When the host wraps an
     [data-hld-upsell] block (see snippets/hld-upsell-picks.liquid), any ticked add-on
     is posted alongside the main product in ONE /cart/add.js call using the items[]
     form — so the shopper gets a single request, a single drawer open, and the two
     lines can never half-land. The picker sits outside the {% form %} on purpose, so
     its checkboxes are read from the DOM here rather than from FormData. */
  if (!customElements.get('hld-quick-add')) {
    customElements.define('hld-quick-add', class extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        if (!this.form) return;
        this.btn = this.form.querySelector('[name="add"]');
        this.label = this.btn && this.btn.querySelector('span');
        this.form.addEventListener('submit', (e) => this.onSubmit(e));
      }

      /* Ticked add-ons, as /cart/add.js line items. */
      pickedUpsells() {
        return [...this.querySelectorAll('[data-hld-upsell-check]')]
          .filter((c) => c.checked && !c.hasAttribute('data-multi'))
          .map((c) => ({ id: Number(c.getAttribute('data-variant-id')), quantity: 1 }))
          .filter((item) => item.id);
      }

      /* The main product as a line item, read off the same FormData the single-item
         path would have posted — so selling plans and line item properties survive the
         switch to the JSON items[] body instead of being silently dropped. */
      mainItem() {
        const item = { id: 0, quantity: 1 };
        const properties = {};
        new FormData(this.form).forEach((value, key) => {
          if (key === 'id') item.id = Number(value);
          else if (key === 'quantity') item.quantity = Math.max(1, parseInt(value, 10) || 1);
          else if (key === 'selling_plan' && value) item.selling_plan = Number(value);
          else if (key.indexOf('properties[') === 0 && value !== '') {
            properties[key.slice(11, -1)] = value;
          }
        });
        if (Object.keys(properties).length) item.properties = properties;
        return item;
      }

      async onSubmit(e) {
        // No fetch (or already working) → let the form POST to /cart normally.
        if (!window.fetch) return;
        e.preventDefault();
        if (this.loading) return;
        this.loading = true;

        const original = this.label ? this.label.textContent : '';
        if (this.btn) this.btn.setAttribute('aria-busy', 'true');
        if (this.label) this.label.textContent = 'Adding…';

        const picked = this.pickedUpsells();

        try {
          let addRes;
          if (picked.length) {
            addRes = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: [this.mainItem()].concat(picked) }),
            });
          } else {
            addRes = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { Accept: 'application/json' },
              body: new FormData(this.form),
            });
          }

          // items[] is all-or-nothing: one sold-out add-on rejects the whole request.
          // Losing the main product to an accessory that went out of stock mid-session
          // would be the worst outcome here, so retry the main product on its own.
          if (!addRes.ok && picked.length) {
            addRes = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { Accept: 'application/json' },
              body: new FormData(this.form),
            });
          }
          if (!addRes.ok) throw new Error('add-to-cart failed');

          const added = picked.length ? [this.mainItem()].concat(picked) : [this.mainItem()];
          this.clearUpsells();
          await this.refreshCount();
          if (this.label) this.label.textContent = 'Added ✓';
          // detail carries which surface won the add, so the AOV lift can be
          // attributed per surface rather than showing up as one undifferentiated
          // add_to_cart. Listeners that predate this ignore it harmlessly.
          document.dispatchEvent(new CustomEvent('hld:cart:added', {
            bubbles: true,
            detail: { surface: this.surface(), items: added, upsellCount: picked.length },
          }));

          if (this.label) {
            setTimeout(() => { this.label.textContent = original; }, 1600);
          }
        } catch (err) {
          // Fall back to the standard cart page so the add is never lost.
          this.form.submit();
          return;
        } finally {
          this.loading = false;
          if (this.btn) this.btn.removeAttribute('aria-busy');
        }
      }

      /* Which surface this add came from. Read off the DOM rather than passed in as
         an attribute, so a card dropped into a new context reports itself correctly
         without anyone remembering to label it. */
      surface() {
        if (this.closest('.hld-qv')) return 'quick-view';
        if (this.closest('#hld-cart-drawer')) return 'cart-drawer';
        if (this.closest('.hld-upsell--page')) return 'cart-page';
        if (this.closest('.hld-atf__purchase')) return 'pdp';
        if (this.closest('.hld-cross-sell')) return 'cross-sell';
        if (this.closest('.hld-pcard')) return 'product-card';
        return 'other';
      }

      /* Untick after a successful add, so a second click on Add to Cart doesn't
         quietly bill the shopper for the same accessory twice. */
      clearUpsells() {
        const boxes = [...this.querySelectorAll('[data-hld-upsell-check]')].filter((c) => c.checked);
        if (!boxes.length) return;
        boxes.forEach((c) => { c.checked = false; });
        document.dispatchEvent(new CustomEvent('hld:upsell:sync', { bubbles: true }));
      }

      async refreshCount() {
        try {
          const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
          const cart = await res.json();
          document.querySelectorAll('[data-hld-cart-count]').forEach((el) => {
            el.textContent = cart.item_count;
            if (cart.item_count > 0) { el.removeAttribute('hidden'); }
            else { el.setAttribute('hidden', ''); }
          });
        } catch (e) { /* count is cosmetic — ignore */ }
      }
    });
  }

  /* ---- HLD cart : slide-out drawer + AJAX line controls ----------------
     Shared controller for BOTH the /cart page and the header drawer. Qty/remove
     hit /cart/change.js, then each [data-hld-cart] root's [data-hld-cart-refresh]
     region is re-rendered from its own section via the Section Rendering API — so
     all money formatting stays server-side Liquid. Degrades to the plain cart form
     (and the cart-icon's /cart link) when fetch is unavailable. */
  if (window.fetch && !window.__hldCart) {
    window.__hldCart = true;
    // Signal JS-on so the page can hide its manual "Update basket" fallback button.
    document.documentElement.setAttribute('data-hld-cart-js', '');

    const drawer = () => document.getElementById('hld-cart-drawer');

    const openDrawer = () => {
      const d = drawer();
      if (!d) return;
      d.hidden = false;
      void d.offsetWidth; // reflow so the slide-in transition runs
      d.classList.add('is-open');
      setScrollLock(true);
      const close = d.querySelector('[data-hld-drawer-close]');
      if (close) close.focus();
    };

    const closeDrawer = () => {
      const d = drawer();
      if (!d) return;
      d.classList.remove('is-open');
      setScrollLock(false);
      setTimeout(() => { if (d && !d.classList.contains('is-open')) d.hidden = true; }, 340);
    };

    const refreshCounts = async () => {
      try {
        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        const cart = await res.json();
        document.querySelectorAll('[data-hld-cart-count]').forEach((el) => {
          el.textContent = cart.item_count;
          if (cart.item_count > 0) el.removeAttribute('hidden'); else el.setAttribute('hidden', '');
        });
        document.querySelectorAll('[data-hld-cart-count-inline]').forEach((el) => { el.textContent = cart.item_count; });
      } catch (e) { /* cosmetic */ }
    };

    const refreshRoots = async () => {
      const roots = [...document.querySelectorAll('[data-hld-cart]')];
      await Promise.all(roots.map(async (root) => {
        const sectionId = root.getAttribute('data-hld-cart-section');
        const target = root.querySelector('[data-hld-cart-refresh]');
        if (!sectionId || !target) return;
        try {
          const r = await fetch(location.pathname + '?section_id=' + encodeURIComponent(sectionId), { headers: { Accept: 'text/html' } });
          if (!r.ok) return;
          const doc = new DOMParser().parseFromString(await r.text(), 'text/html');
          const fresh = doc.querySelector('[data-hld-cart-refresh]');
          if (fresh) target.innerHTML = fresh.innerHTML;
        } catch (e) { /* keep stale DOM on failure */ }
      }));
    };

    const refreshAll = async () => { await Promise.all([refreshCounts(), refreshRoots()]); };

    const applyChange = async (lineEl, line, quantity) => {
      if (!line) return;
      if (lineEl) lineEl.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: Number(line), quantity: Number(quantity) }),
        });
        if (!res.ok) throw new Error('cart change failed');
        await refreshAll();
      } catch (err) {
        window.location.href = '/cart'; // never lose the change
      }
    };

    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-hld-cart-open]');
      if (opener) { if (drawer()) { e.preventDefault(); openDrawer(); } return; }
      if (e.target.closest('[data-hld-drawer-close]')) { e.preventDefault(); closeDrawer(); return; }

      const btn = e.target.closest('[data-hld-cart-minus],[data-hld-cart-plus],[data-hld-cart-remove]');
      if (!btn) return;
      const lineEl = btn.closest('[data-hld-cart-line]');
      const line = btn.getAttribute('data-line');
      let qty = 0;
      if (!btn.hasAttribute('data-hld-cart-remove')) {
        const input = lineEl && lineEl.querySelector('[data-hld-cart-qty]');
        const cur = input ? (parseInt(input.value, 10) || 0) : 0;
        qty = btn.hasAttribute('data-hld-cart-plus') ? cur + 1 : Math.max(0, cur - 1);
      }
      applyChange(lineEl, line, qty);
    });

    document.addEventListener('change', (e) => {
      const qtyInput = e.target.closest('[data-hld-cart-qty]');
      if (qtyInput) {
        const lineEl = qtyInput.closest('[data-hld-cart-line]');
        applyChange(lineEl, qtyInput.getAttribute('data-line'), Math.max(0, parseInt(qtyInput.value, 10) || 0));
        return;
      }
      const note = e.target.closest('[data-hld-cart-note]');
      if (note) {
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: note.value }),
        }).catch(() => {});
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const d = drawer();
      if (d && d.classList.contains('is-open')) closeDrawer();
    });

    // <hld-quick-add> adds a product then fires this — refresh + slide the drawer open.
    document.addEventListener('hld:cart:added', async () => {
      await refreshAll();
      openDrawer();
    });

    // Bridge Horizon's native add-to-cart into the HLD drawer. The base theme's
    // product-form and quick-add components AJAX-add, then dispatch 'cart:update'
    // (CartAddEvent) on the document — but they expect Horizon's own drawer, which
    // this store replaced with the HLD drawer. So catch their add events and open
    // ours. Qty changes (source 'cart-items-component') are ignored so the drawer
    // doesn't pop open on every line edit.
    document.addEventListener('cart:update', (e) => {
      const data = (e.detail && e.detail.data) || {};
      if (data.didError) return;
      if (data.source === 'product-form-component' || data.source === 'quick-add-component') {
        document.dispatchEvent(new CustomEvent('hld:cart:added', { bubbles: true }));
      }
    });
  }

  /* ---- <hld-search-overlay> : in-page search modal ----------------------
     Replaces the header search icon's full-page trip to /search. Any
     [data-hld-search-open] element opens the overlay in place; the icon stays a
     real <a href="/search"> so no-JS visitors still get the search page.

     Results come from sections/hld-predictive-search.liquid via the Section
     Rendering API on /search/suggest, so all money formatting stays in Liquid. */
  if (!customElements.get('hld-search-overlay')) {
    customElements.define('hld-search-overlay', class extends HTMLElement {
      connectedCallback() {
        this.panel = this.querySelector('.hld-searchov__panel');
        this.input = this.querySelector('[data-hld-search-input]');
        this.results = this.querySelector('[data-hld-search-results]');
        this.clearBtn = this.querySelector('[data-hld-search-clear]');
        this.form = this.querySelector('form');
        this.suggestUrl = this.getAttribute('data-suggest-url') || '/search/suggest';
        this.emptyMarkup = this.results ? this.results.innerHTML : '';
        this.open = false;

        // Delegated so triggers rendered anywhere on the page work, including
        // ones injected later (e.g. a section re-render).
        document.addEventListener('click', (e) => {
          const trigger = e.target.closest('[data-hld-search-open]');
          if (trigger) { e.preventDefault(); this.show(trigger); return; }
          if (e.target.closest('[data-hld-search-close]')) { e.preventDefault(); this.hide(); }
        });

        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.open) { e.preventDefault(); this.hide(); }
        });

        if (this.input) {
          this.input.addEventListener('input', () => this.onInput());
          this.input.addEventListener('keydown', (e) => this.onInputKey(e));
        }
        if (this.clearBtn) {
          this.clearBtn.addEventListener('click', () => {
            if (!this.input) return;
            this.input.value = '';
            this.input.focus();
            this.onInput();
          });
        }
        // Empty query → let the form fall through to /search rather than
        // reloading the current page with ?q=.
        if (this.form) {
          this.form.addEventListener('submit', (e) => {
            if (this.input && this.input.value.trim() === '') e.preventDefault();
          });
        }
        // Keep focus inside the panel while it's open.
        this.addEventListener('keydown', (e) => { if (e.key === 'Tab') this.trapFocus(e); });
      }

      /* ---- open / close ---- */
      show(trigger) {
        if (this.open) return;
        this.opener = trigger || null;
        this.scrollY = window.scrollY || window.pageYOffset || 0;

        this.hidden = false;
        // Read layout on the now-displayed panel so the transition has a start
        // state before the class flips — same trick as openDrawer() above.
        // Deliberately not requestAnimationFrame: rAF is throttled in background
        // tabs, which would leave the panel stuck invisible if a shopper opened
        // search and tabbed away.
        if (this.panel) void this.panel.offsetWidth;
        this.classList.add('is-open');
        this.open = true;

        // Lock the page behind the overlay through the same <html> mechanism the
        // drawer and mobile menu use. Never `overflow: hidden` on body — see the
        // scroll-lock note at the top of this file.
        setScrollLock(true);

        // preventScroll matters: the panel is position: fixed at the top of the
        // viewport, so a plain focus() makes the browser scroll the document to 0
        // to "reveal" it — the shopper loses their place on the page behind.
        if (this.input) { this.input.focus({ preventScroll: true }); this.input.select(); }
      }

      hide() {
        if (!this.open) return;
        this.classList.remove('is-open');
        this.open = false;

        // Don't unlock if the cart drawer or mobile menu is also open — they
        // share the one <html> lock and would be left scrolling behind their own
        // backdrop.
        if (!document.querySelector('.hld-drawer.is-open, .hld-navbar__menu.is-open')) setScrollLock(false);

        // preventScroll is load-bearing, not politeness. The trigger lives in the
        // sticky header, and a plain focus() scrolls its LAYOUT box into view —
        // the top of the document — so closing the overlay threw the shopper back
        // up the page. Focus first, then restore, so nothing can scroll after us.
        if (this.opener && document.contains(this.opener)) this.opener.focus({ preventScroll: true });
        // The page was never taken out of flow, so the offset is already intact —
        // this is a belt-and-braces restore for browsers that clamp on unlock.
        // Only correct the offset if something actually moved it — a browser that
        // clamps scroll while <html> is locked, say. `behavior: instant` because the
        // theme sets scroll-behavior: smooth globally, and a restore that visibly
        // glides back is worse than the jump it's fixing.
        if (typeof this.scrollY === 'number' && Math.abs(window.scrollY - this.scrollY) > 1) {
          window.scrollTo({ top: this.scrollY, left: 0, behavior: 'instant' });
        }

        setTimeout(() => {
          if (this.open) return;
          this.hidden = true;
          if (this.input) this.input.value = '';
          this.resetResults();
        }, 260);
      }

      /* ---- predictive results ---- */
      onInput() {
        const term = this.input ? this.input.value.trim() : '';
        if (this.clearBtn) this.clearBtn.hidden = term === '';
        clearTimeout(this.debounce);
        if (term.length < 2) { this.resetResults(); return; }
        this.debounce = setTimeout(() => this.fetchResults(term), 250);
      }

      async fetchResults(term) {
        if (!this.results || !window.fetch) return;
        if (this.controller) this.controller.abort();
        this.controller = 'AbortController' in window ? new AbortController() : null;

        const url = this.suggestUrl
          + '?q=' + encodeURIComponent(term)
          + '&section_id=hld-predictive-search'
          + '&resources[type]=product,collection,query'
          + '&resources[limit]=6'
          + '&resources[options][unavailable_products]=last'
          + '&resources[options][fields]=title,product_type,variants.title,vendor';

        this.results.setAttribute('aria-busy', 'true');
        try {
          const res = await fetch(url, {
            headers: { Accept: 'text/html' },
            signal: this.controller ? this.controller.signal : undefined,
          });
          if (!res.ok) throw new Error('suggest failed');
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const fresh = doc.querySelector('.hld-psearch');
          this.results.innerHTML = fresh ? fresh.outerHTML : this.emptyMarkup;
          if (this.input) this.input.setAttribute('aria-expanded', String(!!fresh));
        } catch (err) {
          if (err.name === 'AbortError') return; // superseded by a newer keystroke
          this.resetResults(); // fall back to the hint; Enter still runs a real search
        } finally {
          this.results.setAttribute('aria-busy', 'false');
        }
      }

      resetResults() {
        if (!this.results) return;
        this.results.innerHTML = this.emptyMarkup;
        this.results.setAttribute('aria-busy', 'false');
        if (this.input) this.input.setAttribute('aria-expanded', 'false');
      }

      /* ---- keyboard ---- */
      onInputKey(e) {
        if (e.key !== 'ArrowDown') return;
        const first = this.results && this.results.querySelector('[data-hld-result]');
        if (first) { e.preventDefault(); first.focus(); }
      }

      trapFocus(e) {
        const focusables = [...this.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"])')]
          .filter((el) => !el.hidden && el.offsetParent !== null);
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });
  }

  /* ---- <hld-menu-toggle> : mobile nav ---------------------------------- */
  if (!customElements.get('hld-menu-toggle')) {
    customElements.define('hld-menu-toggle', class extends HTMLElement {
      connectedCallback() {
        this.btn = this.querySelector('button');
        this.targetEl = document.getElementById(this.getAttribute('data-target'));
        if (!this.btn || !this.targetEl) return;
        this.backdrop = document.querySelector('[data-hld-menu-backdrop]');
        this.closeBtn = this.targetEl.querySelector('[data-hld-menu-close]');

        const setOpen = (open) => {
          this.targetEl.classList.toggle('is-open', open);
          if (this.backdrop) this.backdrop.classList.toggle('is-open', open);
          this.btn.setAttribute('aria-expanded', String(open));
          setScrollLock(open);
        };

        this.btn.addEventListener('click', () => setOpen(!this.targetEl.classList.contains('is-open')));
        if (this.closeBtn) this.closeBtn.addEventListener('click', () => setOpen(false));
        if (this.backdrop) this.backdrop.addEventListener('click', () => setOpen(false));
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.targetEl.classList.contains('is-open')) setOpen(false);
        });
      }
    });
  }
})();
